import React, { useMemo, useState } from "react";
import { Add } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import PageBand from "../../layouts/page-detail/PageBand";
import SearchBox from "../../components/form/SearchBox/SearchBox";
import { SEARCHBOX } from "../../data/constants";
import WorkflowDefinitionCard, {
  WorkflowDefinition,
} from "../../features/workflows/WorkflowDefinitionCard";
import "./WorkflowLibrary.scss";

type StatusFilter = "active" | "draft" | "published" | "archived" | "all";

const ALL_CATEGORIES = "all";
// TODO Epic 80372 / 80428: replace with the real library-wide instance count from the API.
// This placeholder does not react to the search or status/category filters.
const LIBRARY_INSTANCE_TOTAL = 4;

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

// Same value PifBreadcrumbs uses for its on-banner links — plain inherited text is unreadable
// against the brown band.
const BREADCRUMB_LINK_COLOR = "rgba(255, 255, 255, 0.75)";

const WORKFLOW_DEFINITIONS: WorkflowDefinition[] = [
  {
    id: 1,
    name: "New Hire — Post-Interview Form",
    category: "Hiring",
    status: "published",
    description:
      "Collect candidate details and complete the three-signature approval chain.",
    version: 3,
    stageCount: 9,
    activeInstanceCount: 6,
    editedOn: "2026-08-14",
    firstStageType: "form",
    isFlagship: true,
  },
  {
    id: 2,
    name: "Employee Termination / Offboarding",
    category: "Offboarding",
    status: "draft",
    description:
      "Coordinate payroll notice, equipment return, and access revocation.",
    version: 1,
    stageCount: 5,
    activeInstanceCount: 1,
    editedOn: "2026-08-11",
    firstStageType: "approval",
  },
  {
    id: 3,
    name: "Applicant Intake — Workflow Zero",
    category: "Recruiting",
    status: "published",
    description:
      "Guide applicant intake from recruiting review through HR screening.",
    version: 2,
    stageCount: 4,
    activeInstanceCount: 3,
    editedOn: "2026-08-09",
    firstStageType: "task",
  },
  {
    id: 4,
    name: "Internal Transfer Request",
    category: "Hiring",
    status: "published",
    description:
      "Move an employee between departments with manager sign-off and payroll update.",
    version: 2,
    stageCount: 7,
    activeInstanceCount: 2,
    editedOn: "2026-08-16",
    firstStageType: "form",
  },
  {
    id: 5,
    name: "Leave of Absence Request",
    category: "Benefits",
    status: "published",
    description:
      "Collect leave dates, coverage plan, and route to the supervisor then HR.",
    version: 1,
    stageCount: 6,
    activeInstanceCount: 4,
    editedOn: "2026-08-12",
    firstStageType: "approval",
  },
  {
    id: 6,
    name: "Background Check Renewal",
    category: "Compliance",
    status: "draft",
    description:
      "Remind the assigned coordinator to recertify fingerprints before they expire.",
    version: 1,
    stageCount: 3,
    activeInstanceCount: 0,
    editedOn: "2026-08-18",
    firstStageType: "task",
  },
  {
    id: 7,
    name: "Annual Credential Review",
    category: "Compliance",
    status: "archived",
    description:
      "Previous credential-review flow retained for historical workflow instances.",
    version: 4,
    stageCount: 6,
    activeInstanceCount: 0,
    editedOn: "2026-06-24",
    firstStageType: "form",
  },
];

const WorkflowLibrary: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [announcement, setAnnouncement] = useState("");

  const categories = useMemo(
    () =>
      Array.from(new Set(WORKFLOW_DEFINITIONS.map((definition) => definition.category))).sort(
        (left, right) => left.localeCompare(right),
      ),
    [],
  );

  const visibleDefinitions = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return WORKFLOW_DEFINITIONS.filter((definition) => {
      const matchesStatus =
        status === "all" ||
        (status === "active" && definition.status !== "archived") ||
        definition.status === status;
      const matchesCategory =
        category === ALL_CATEGORIES || definition.category === category;
      const matchesSearch =
        !query ||
        definition.name.toLowerCase().includes(query) ||
        definition.category.toLowerCase().includes(query);

      return matchesStatus && matchesCategory && matchesSearch;
    });
  }, [category, searchText, status]);

  const handleStatusChange = (event: SelectChangeEvent<StatusFilter>) => {
    setStatus(event.target.value as StatusFilter);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setCategory(event.target.value);
  };

  const handleCardAction = (action: string, definition: WorkflowDefinition) => {
    setAnnouncement(`${action} for ${definition.name} is available in a later workflow UI slice.`);
  };

  const handleNewWorkflow = () => {
    setAnnouncement("New workflow is available in the Workflow Builder UI.");
  };

  return (
    <main className="workflow-library">
      {/* App.tsx skips its own PageDetail for /workflows, so the page supplies the title and trail. */}
      <PageBand
        title="Workflow Library"
        breadcrumbs={
          <Breadcrumbs
            aria-label="breadcrumbs"
            sx={{ "& .MuiBreadcrumbs-separator": { color: BREADCRUMB_LINK_COLOR } }}
          >
            <Typography sx={{ color: BREADCRUMB_LINK_COLOR }}>Workflow Management</Typography>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Library</Typography>
          </Breadcrumbs>
        }
      />

      <section className="workflow-library__content" aria-label="Workflow definitions">
        <Box className="workflow-library__toolbar">
          <Box className="search-box-wrap workflow-library__search">
            <SearchBox searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER} onSearch={setSearchText} />
          </Box>

          <FormControl className="workflow-library__filter" size="small">
            <InputLabel id="workflow-status-label">Status</InputLabel>
            <Select
              labelId="workflow-status-label"
              label="Status"
              value={status}
              onChange={handleStatusChange}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>

          <FormControl className="workflow-library__filter" size="small">
            <InputLabel id="workflow-category-label">Category</InputLabel>
            <Select
              labelId="workflow-category-label"
              label="Category"
              value={category}
              onChange={handleCategoryChange}
            >
              <MenuItem value={ALL_CATEGORIES}>All categories</MenuItem>
              {categories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleNewWorkflow}
            className="workflow-library__new-button"
          >
            New workflow
          </Button>
        </Box>

        <Typography className="workflow-library__count" aria-live="polite">
          {pluralize(visibleDefinitions.length, "definition")} ·{" "}
          {pluralize(LIBRARY_INSTANCE_TOTAL, "instance")}
        </Typography>

        {visibleDefinitions.length > 0 ? (
          <Box className="workflow-library__grid">
            {visibleDefinitions.map((definition) => (
              <WorkflowDefinitionCard
                key={definition.id}
                definition={definition}
                onAction={handleCardAction}
              />
            ))}
          </Box>
        ) : (
          <Box className="workflow-library__empty">
            <Typography component="h2">No workflow definitions found</Typography>
            <Typography>Try changing the search or filters.</Typography>
          </Box>
        )}

        <Typography className="workflow-library__announcement" role="status" aria-live="polite">
          {announcement}
        </Typography>
      </section>
    </main>
  );
};

export default WorkflowLibrary;
